import { penPathsApi, PenPathInstance } from './api';
import {
  AnchorPoint,
  subdivideBezier,
  distanceToSegment,
  calculatePathCenter
} from '../types/penPath';

/**
 * Manages pen path instances for a map
 * Handles loading, saving, and manipulating paths with region-based storage
 */
export class PenPathManager {
  private mapId: string;
  private paths: Map<string, PenPathInstance> = new Map();
  private loadedRegions: Set<string> = new Set();
  private dirtyPaths: Set<string> = new Set();
  private newPaths: Set<string> = new Set();
  private deletedPaths: Set<string> = new Set();
  private saveTimeout: NodeJS.Timeout | null = null;
  private onPathsChanged: (() => void) | null = null;
  private onSaveStateChanged: ((isSaving: boolean, hasUnsaved: boolean) => void) | null = null;
  private onBeforeSave: (() => Promise<void>) | null = null;
  private isSaving: boolean = false;
  private autoSaveEnabled: boolean = true;
  private nextLocalId: number = 1;

  private static readonly SAVE_DELAY = 2000;
  private static readonly REGION_SIZE = 2048;

  /**
   * Creates a new PenPathManager for a map
   * @param mapId The map ID to manage paths for
   */
  constructor(mapId: string) {
    this.mapId = mapId;
  }

  /**
   * Set callback for when paths change
   * @param callback Callback function or null to clear
   */
  setOnPathsChanged(callback: (() => void) | null): void {
    this.onPathsChanged = callback;
  }

  /**
   * Set callback for when save state changes
   * @param callback Callback function or null to clear
   */
  setOnSaveStateChanged(callback: ((isSaving: boolean, hasUnsaved: boolean) => void) | null): void {
    this.onSaveStateChanged = callback;
  }

  /**
   * Set callback to run before saving (e.g., to save layers first)
   * @param callback Callback function or null to clear
   */
  setOnBeforeSave(callback: (() => Promise<void>) | null): void {
    this.onBeforeSave = callback;
  }

  /**
   * Enable or disable autosave
   * @param enabled Whether autosave should be enabled
   */
  setAutoSaveEnabled(enabled: boolean): void {
    this.autoSaveEnabled = enabled;
    if (!enabled && this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
  }

  /**
   * Notify save state changed
   */
  private notifySaveStateChanged(): void {
    const hasUnsaved = this.dirtyPaths.size > 0 || this.newPaths.size > 0 || this.deletedPaths.size > 0;
    this.onSaveStateChanged?.(this.isSaving, hasUnsaved);
  }

  /**
   * Load all pen paths for the map
   */
  async loadAllPaths(): Promise<void> {
    try {
      const response = await penPathsApi.list(this.mapId);
      this.paths.clear();

      for (const path of response.penPaths) {
        this.paths.set(path.id, path);
      }

      this.notifyPathsChanged();
    } catch (err) {
      console.error('Failed to load pen paths:', err);
    }
  }

  /**
   * Load pen paths for a specific region
   * @param regionX Region X coordinate
   * @param regionY Region Y coordinate
   */
  async loadRegionPaths(regionX: number, regionY: number): Promise<void> {
    const key = `${regionX},${regionY}`;
    if (this.loadedRegions.has(key)) return;

    try {
      const response = await penPathsApi.getRegionPaths(this.mapId, regionX, regionY);
      this.loadedRegions.add(key);

      for (const path of response.penPaths) {
        this.paths.set(path.id, { ...path, regionX, regionY });
      }

      this.notifyPathsChanged();
    } catch (err) {
      console.error(`Failed to load pen paths for region ${regionX},${regionY}:`, err);
    }
  }

  /**
   * Ensure pen paths are loaded for visible regions
   * @param viewportX Viewport center X
   * @param viewportY Viewport center Y
   * @param viewportWidth Viewport width
   * @param viewportHeight Viewport height
   * @param zoom Current zoom level
   */
  async ensureRegionsLoaded(
    viewportX: number,
    viewportY: number,
    viewportWidth: number,
    viewportHeight: number,
    zoom: number
  ): Promise<void> {
    const halfWidth = viewportWidth / (2 * zoom);
    const halfHeight = viewportHeight / (2 * zoom);

    const minX = viewportX - halfWidth;
    const maxX = viewportX + halfWidth;
    const minY = viewportY - halfHeight;
    const maxY = viewportY + halfHeight;

    let minRegionX = Math.floor(minX / PenPathManager.REGION_SIZE);
    let maxRegionX = Math.floor(maxX / PenPathManager.REGION_SIZE);
    let minRegionY = Math.floor(minY / PenPathManager.REGION_SIZE);
    let maxRegionY = Math.floor(maxY / PenPathManager.REGION_SIZE);

    const MAX_REGIONS_PER_AXIS = 10;
    const regionsX = maxRegionX - minRegionX + 1;
    const regionsY = maxRegionY - minRegionY + 1;

    if (regionsX > MAX_REGIONS_PER_AXIS) {
      const centerRegionX = Math.floor(viewportX / PenPathManager.REGION_SIZE);
      const halfRegions = Math.floor(MAX_REGIONS_PER_AXIS / 2);
      minRegionX = centerRegionX - halfRegions;
      maxRegionX = centerRegionX + halfRegions;
    }

    if (regionsY > MAX_REGIONS_PER_AXIS) {
      const centerRegionY = Math.floor(viewportY / PenPathManager.REGION_SIZE);
      const halfRegions = Math.floor(MAX_REGIONS_PER_AXIS / 2);
      minRegionY = centerRegionY - halfRegions;
      maxRegionY = centerRegionY + halfRegions;
    }

    const loadPromises: Promise<void>[] = [];

    for (let rx = minRegionX; rx <= maxRegionX; rx++) {
      for (let ry = minRegionY; ry <= maxRegionY; ry++) {
        loadPromises.push(this.loadRegionPaths(rx, ry));
      }
    }

    await Promise.all(loadPromises);
  }

  /**
   * Get all paths sorted by zIndex
   * @returns Array of pen path instances
   */
  getPaths(): PenPathInstance[] {
    return Array.from(this.paths.values()).sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
  }

  /**
   * Get a single path by ID
   * @param pathId The path ID
   * @returns The path instance or undefined
   */
  getPath(pathId: string): PenPathInstance | undefined {
    return this.paths.get(pathId);
  }

  /**
   * Add a new pen path locally
   * @param pathData Path data to add
   * @returns The created path instance
   */
  addPath(pathData: {
    points: AnchorPoint[];
    closed: boolean;
    strokeColor?: string | null;
    strokeWidth?: number;
    strokeOpacity?: number;
    lineCap?: 'butt' | 'round' | 'square';
    lineJoin?: 'miter' | 'round' | 'bevel';
    dashPreset?: 'solid' | 'dashed' | 'dotted';
    arrowStart?: 'none' | 'arrow' | 'triangle' | 'circle' | 'square' | 'diamond';
    arrowEnd?: 'none' | 'arrow' | 'triangle' | 'circle' | 'square' | 'diamond';
    fillColor?: string | null;
    fillOpacity?: number;
    featherRadius?: number;
    layerId?: string;
  }): PenPathInstance {
    const localId = `local_penpath_${Date.now()}_${this.nextLocalId++}`;

    // Calculate center from points
    const center = calculatePathCenter(pathData.points);

    // Calculate region from center
    const regionX = Math.floor(center.x / PenPathManager.REGION_SIZE);
    const regionY = Math.floor(center.y / PenPathManager.REGION_SIZE);

    const path: PenPathInstance = {
      id: localId,
      points: pathData.points,
      closed: pathData.closed,
      strokeColor: pathData.strokeColor ?? '#000000',
      strokeWidth: pathData.strokeWidth ?? 2,
      strokeOpacity: pathData.strokeOpacity ?? 1,
      lineCap: pathData.lineCap ?? 'round',
      lineJoin: pathData.lineJoin ?? 'round',
      dashPreset: pathData.dashPreset ?? 'solid',
      arrowStart: pathData.arrowStart ?? 'none',
      arrowEnd: pathData.arrowEnd ?? 'none',
      fillColor: pathData.fillColor ?? null,
      fillOpacity: pathData.fillOpacity ?? 1,
      featherRadius: pathData.featherRadius ?? 0,
      x: center.x,
      y: center.y,
      rotation: 0,
      scale: 1,
      zIndex: this.getMaxZIndex() + 1,
      layerId: pathData.layerId ?? 'default',
      regionX,
      regionY
    };

    this.paths.set(localId, path);
    this.newPaths.add(localId);
    this.scheduleSave();
    this.notifyPathsChanged();
    this.notifySaveStateChanged();

    return path;
  }

  /**
   * Update a path locally (with debounced save)
   * @param pathId The path ID to update
   * @param updates Partial updates to apply
   */
  updatePathLocal(pathId: string, updates: Partial<PenPathInstance>): void {
    const path = this.paths.get(pathId);
    if (!path) return;

    Object.assign(path, updates);

    // Recalculate region if position changed
    if (updates.x !== undefined || updates.y !== undefined) {
      path.regionX = Math.floor(path.x / PenPathManager.REGION_SIZE);
      path.regionY = Math.floor(path.y / PenPathManager.REGION_SIZE);
    }

    this.dirtyPaths.add(pathId);
    this.scheduleSave();
    this.notifyPathsChanged();
    this.notifySaveStateChanged();
  }

  /**
   * Delete a path locally
   * @param pathId The path ID to delete
   * @returns True if the path was deleted
   */
  deletePath(pathId: string): boolean {
    if (!this.paths.has(pathId)) return false;

    this.paths.delete(pathId);
    this.dirtyPaths.delete(pathId);

    if (this.newPaths.has(pathId)) {
      this.newPaths.delete(pathId);
    } else {
      this.deletedPaths.add(pathId);
    }

    this.scheduleSave();
    this.notifyPathsChanged();
    this.notifySaveStateChanged();

    return true;
  }

  /**
   * Move paths to a different layer
   * @param pathIds Array of path IDs to move
   * @param targetLayerId Target layer ID
   */
  movePathsToLayer(pathIds: string[], targetLayerId: string): void {
    for (const pathId of pathIds) {
      const path = this.paths.get(pathId);
      if (path) {
        path.layerId = targetLayerId;
        this.dirtyPaths.add(pathId);
      }
    }
    this.scheduleSave();
    this.notifyPathsChanged();
    this.notifySaveStateChanged();
  }

  /**
   * Get paths on a specific layer
   * @param layerId Layer ID to filter by
   * @returns Array of paths on the layer
   */
  getPathsOnLayer(layerId: string): PenPathInstance[] {
    return this.getPaths().filter(p => (p.layerId || 'default') === layerId);
  }

  /**
   * Delete all paths on specified layers
   * @param layerIds Array of layer IDs to delete paths from
   */
  deletePathsOnLayers(layerIds: string[]): void {
    const layerIdSet = new Set(layerIds);
    const pathsToDelete: string[] = [];

    for (const path of this.paths.values()) {
      const pathLayerId = path.layerId || 'default';
      if (layerIdSet.has(pathLayerId)) {
        pathsToDelete.push(path.id);
      }
    }

    for (const pathId of pathsToDelete) {
      this.paths.delete(pathId);
      this.dirtyPaths.delete(pathId);

      if (this.newPaths.has(pathId)) {
        this.newPaths.delete(pathId);
      } else {
        this.deletedPaths.add(pathId);
      }
    }

    if (pathsToDelete.length > 0) {
      this.scheduleSave();
      this.notifyPathsChanged();
      this.notifySaveStateChanged();
    }
  }

  /**
   * Find path at a given world position using Bezier curve hit testing
   * @param worldX World X coordinate
   * @param worldY World Y coordinate
   * @param tolerance Hit tolerance in pixels
   * @param activeLayerId Only find paths on this layer (or null for all)
   * @returns The path at the position or null
   */
  findPathAtPosition(
    worldX: number,
    worldY: number,
    tolerance: number = 10,
    activeLayerId: string | null = null
  ): PenPathInstance | null {
    // Iterate in reverse z-order (top to bottom)
    const sortedPaths = this.getPaths().reverse();

    for (const path of sortedPaths) {
      // Only find paths on the active layer if specified
      if (activeLayerId !== null) {
        const pathLayerId = path.layerId || 'default';
        if (pathLayerId !== activeLayerId) continue;
      }

      if (path.points.length < 1) continue;

      // Check if point is near the path
      if (this.isPointNearPath(worldX, worldY, path, tolerance)) {
        return path;
      }
    }

    return null;
  }

  /**
   * Check if a point is near a path using Bezier subdivision
   * @param px Point X
   * @param py Point Y
   * @param path The path to check
   * @param tolerance Distance tolerance
   * @returns True if point is near the path
   */
  private isPointNearPath(
    px: number,
    py: number,
    path: PenPathInstance,
    tolerance: number
  ): boolean {
    const points = path.points;

    // Check if near any segment
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i];
      const p1 = points[i + 1];

      // Get control points
      const cp1 = p0.handleOut
        ? { x: p0.x + p0.handleOut.x, y: p0.y + p0.handleOut.y }
        : { x: p0.x, y: p0.y };
      const cp2 = p1.handleIn
        ? { x: p1.x + p1.handleIn.x, y: p1.y + p1.handleIn.y }
        : { x: p1.x, y: p1.y };

      // Subdivide the Bezier curve and check distance to each segment
      const subdivided = subdivideBezier(
        { x: p0.x, y: p0.y },
        cp1,
        cp2,
        { x: p1.x, y: p1.y },
        20
      );

      for (let j = 0; j < subdivided.length - 1; j++) {
        const dist = distanceToSegment(
          px, py,
          subdivided[j].x, subdivided[j].y,
          subdivided[j + 1].x, subdivided[j + 1].y
        );
        if (dist <= tolerance) {
          return true;
        }
      }
    }

    // If closed, check the closing segment
    if (path.closed && points.length > 1) {
      const p0 = points[points.length - 1];
      const p1 = points[0];

      const cp1 = p0.handleOut
        ? { x: p0.x + p0.handleOut.x, y: p0.y + p0.handleOut.y }
        : { x: p0.x, y: p0.y };
      const cp2 = p1.handleIn
        ? { x: p1.x + p1.handleIn.x, y: p1.y + p1.handleIn.y }
        : { x: p1.x, y: p1.y };

      const subdivided = subdivideBezier(
        { x: p0.x, y: p0.y },
        cp1,
        cp2,
        { x: p1.x, y: p1.y },
        20
      );

      for (let j = 0; j < subdivided.length - 1; j++) {
        const dist = distanceToSegment(
          px, py,
          subdivided[j].x, subdivided[j].y,
          subdivided[j + 1].x, subdivided[j + 1].y
        );
        if (dist <= tolerance) {
          return true;
        }
      }
    }

    // Also check if inside a filled closed path
    if (path.closed && path.fillColor !== null && points.length > 2) {
      if (this.isPointInsidePath(px, py, path)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if a point is inside a closed path using ray casting
   * @param px Point X
   * @param py Point Y
   * @param path The closed path
   * @returns True if point is inside
   */
  private isPointInsidePath(px: number, py: number, path: PenPathInstance): boolean {
    // Approximate the path with line segments
    const allPoints: { x: number; y: number }[] = [];
    const points = path.points;

    for (let i = 0; i < points.length; i++) {
      const p0 = points[i];
      const p1 = points[(i + 1) % points.length];

      const cp1 = p0.handleOut
        ? { x: p0.x + p0.handleOut.x, y: p0.y + p0.handleOut.y }
        : { x: p0.x, y: p0.y };
      const cp2 = p1.handleIn
        ? { x: p1.x + p1.handleIn.x, y: p1.y + p1.handleIn.y }
        : { x: p1.x, y: p1.y };

      const subdivided = subdivideBezier(
        { x: p0.x, y: p0.y },
        cp1,
        cp2,
        { x: p1.x, y: p1.y },
        10
      );

      // Skip the last point to avoid duplicates
      for (let j = 0; j < subdivided.length - 1; j++) {
        allPoints.push(subdivided[j]);
      }
    }

    // Ray casting algorithm
    let inside = false;
    for (let i = 0, j = allPoints.length - 1; i < allPoints.length; j = i++) {
      const xi = allPoints[i].x;
      const yi = allPoints[i].y;
      const xj = allPoints[j].x;
      const yj = allPoints[j].y;

      const intersect = ((yi > py) !== (yj > py)) &&
        (px < (xj - xi) * (py - yi) / (yj - yi) + xi);

      if (intersect) inside = !inside;
    }

    return inside;
  }

  /**
   * Find all paths within a rectangular area
   * @param x1 First corner X
   * @param y1 First corner Y
   * @param x2 Second corner X
   * @param y2 Second corner Y
   * @param activeLayerId Only find paths on this layer (or null for all)
   * @returns Array of paths in the rectangle
   */
  findPathsInRect(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    activeLayerId: string | null = null
  ): PenPathInstance[] {
    const minX = Math.min(x1, x2);
    const maxX = Math.max(x1, x2);
    const minY = Math.min(y1, y2);
    const maxY = Math.max(y1, y2);

    const result: PenPathInstance[] = [];

    for (const path of this.paths.values()) {
      // Only find paths on the active layer if specified
      if (activeLayerId !== null) {
        const pathLayerId = path.layerId || 'default';
        if (pathLayerId !== activeLayerId) continue;
      }

      // Check if any point is in the rectangle
      let intersects = false;
      for (const point of path.points) {
        if (point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY) {
          intersects = true;
          break;
        }
      }

      if (intersects) {
        result.push(path);
      }
    }

    return result;
  }

  /**
   * Get the maximum z-index of all paths
   * @returns Maximum z-index value
   */
  getMaxZIndex(): number {
    let maxZ = 0;
    for (const path of this.paths.values()) {
      maxZ = Math.max(maxZ, path.zIndex || 0);
    }
    return maxZ;
  }

  /**
   * Schedule a debounced save
   */
  private scheduleSave(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }

    if (this.autoSaveEnabled) {
      this.saveTimeout = setTimeout(() => {
        this.saveDirtyPaths();
      }, PenPathManager.SAVE_DELAY);
    }
  }

  /**
   * Save all pending changes
   */
  async saveDirtyPaths(): Promise<void> {
    if (this.dirtyPaths.size === 0 && this.newPaths.size === 0 && this.deletedPaths.size === 0) return;

    // Run before-save callback (e.g., to save layers first and update local IDs)
    if (this.onBeforeSave) {
      await this.onBeforeSave();
    }

    this.isSaving = true;
    this.notifySaveStateChanged();

    try {
      // 1. Delete paths marked for deletion
      for (const pathId of this.deletedPaths) {
        try {
          await penPathsApi.delete(this.mapId, pathId);
        } catch (err) {
          console.error(`Failed to delete pen path ${pathId}:`, err);
        }
      }
      this.deletedPaths.clear();

      // 2. Create new paths
      const idMapping = new Map<string, string>();
      for (const localId of this.newPaths) {
        const path = this.paths.get(localId);
        if (path) {
          try {
            const { id, regionX, regionY, ...pathData } = path;
            const response = await penPathsApi.create(this.mapId, pathData as Omit<PenPathInstance, 'id' | 'regionX' | 'regionY'>);

            this.paths.delete(localId);
            const serverPath = { ...response.penPath, regionX: response.regionX, regionY: response.regionY };
            this.paths.set(serverPath.id, serverPath);
            idMapping.set(localId, serverPath.id);
          } catch (err) {
            console.error(`Failed to create pen path:`, err);
          }
        }
      }
      this.newPaths.clear();

      // 3. Update dirty paths
      for (const pathId of this.dirtyPaths) {
        const path = this.paths.get(pathId);
        if (path) {
          try {
            await penPathsApi.update(this.mapId, path.id, path);
          } catch (err) {
            console.error(`Failed to update pen path ${path.id}:`, err);
          }
        }
      }
      this.dirtyPaths.clear();

      this.notifyPathsChanged();
    } catch (err) {
      console.error('Failed to save pen paths:', err);
    } finally {
      this.isSaving = false;
      this.notifySaveStateChanged();
    }
  }

  /**
   * Check if there are unsaved changes
   * @returns True if there are unsaved changes
   */
  hasUnsavedChanges(): boolean {
    return this.dirtyPaths.size > 0 || this.newPaths.size > 0 || this.deletedPaths.size > 0;
  }

  /**
   * Notify that paths have changed
   */
  private notifyPathsChanged(): void {
    this.onPathsChanged?.();
  }

  /**
   * Update layer IDs when layers get new server IDs
   * Called when LayerManager saves new layers and gets back server IDs
   * @param idMapping Map of local IDs to server IDs
   */
  updateLayerIds(idMapping: Map<string, string>): void {
    let changed = false;
    for (const path of this.paths.values()) {
      if (path.layerId && idMapping.has(path.layerId)) {
        path.layerId = idMapping.get(path.layerId)!;
        this.dirtyPaths.add(path.id);
        changed = true;
      }
    }
    if (changed) {
      this.scheduleSave();
      this.notifyPathsChanged();
      this.notifySaveStateChanged();
    }
  }

  /**
   * Cleanup
   */
  destroy(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
  }
}
